import { useId, useState } from 'react';

import {
  PLATFORM_LABELS,
  applyPerSubmissionCap,
  cpmEarningCents,
  formatCents,
  type Platform,
} from '@nexus/shared';

import { Facts, Fact, FieldRow, SelectField, TextField, formatNumber } from '../ui';

/**
 * "What would that be worth?"
 *
 * Every platform in this market puts one of these on the page a creator lands
 * on, and it is not marketing: a rate quoted as cents per thousand views is a
 * unit nobody has intuition for, and a creator who cannot convert it into a
 * number they recognise cannot decide whether to spend an evening editing.
 *
 * ## It is arithmetic, and it says so
 *
 * The sum is `cpmEarningCents` — the SAME function the earning is actually
 * computed with, not a second copy of the formula that could round differently
 * — and the page says in words that it is arithmetic rather than a promise.
 * The one thing a calculator on a payouts site must never do is read as an
 * offer: views are not guaranteed, budgets run out, and a clip that is rejected
 * earns nothing. All three of those are said here rather than in a footnote.
 *
 * ## Why the rate is an input on the landing page and fixed on a campaign
 *
 * On a campaign card the rate is that campaign's own and cannot be edited: the
 * figure has to be the one the creator would really be paid. On the public
 * landing there is no campaign yet, so the rate is theirs to set and the page
 * says that campaigns set their own.
 *
 * ## The platform, and the cap
 *
 * A campaign that pays differently per platform hands this a rate per platform
 * and the estimator asks which one — a single "rate used" line under a figure
 * that could be either would be a figure a creator cannot check. The per-clip
 * cap is applied with the same `applyPerSubmissionCap` the calculator uses, so
 * "$50" here is "$50" there.
 */
export function Estimator({
  ratePerThousandCents,
  platformRates,
  perSubmissionCapCents = null,
  editableRate,
  defaultViews = 25_000,
  defaultClips = 4,
}: {
  /** The campaign's rate, or the starting point when the rate is editable. */
  ratePerThousandCents: number;
  /** One rate per allowed platform; a select appears when they differ. */
  platformRates?: Array<{ platform: Platform; ratePerThousandCents: number }>;
  /** The most one clip can earn on the campaign. Null is no cap. */
  perSubmissionCapCents?: number | null;
  editableRate?: boolean;
  defaultViews?: number;
  defaultClips?: number;
}): React.JSX.Element {
  const [views, setViews] = useState(defaultViews);
  const [clips, setClips] = useState(defaultClips);
  const [rate, setRate] = useState(ratePerThousandCents);
  const [platform, setPlatform] = useState<Platform | ''>(platformRates?.[0]?.platform ?? '');
  const captionId = useId();

  const rates = platformRates ?? [];
  const ratesDiffer = new Set(rates.map((r) => r.ratePerThousandCents)).size > 1;
  const platformRate = rates.find((r) => r.platform === platform)?.ratePerThousandCents;
  const effectiveRate = editableRate
    ? rate
    : ratesDiffer && platformRate !== undefined
      ? platformRate
      : (rates[0]?.ratePerThousandCents ?? ratePerThousandCents);
  const perClip = applyPerSubmissionCap(cpmEarningCents(views, effectiveRate), perSubmissionCapCents);
  const perMonth = perClip * Math.max(0, clips);

  return (
    <div className="stack tight">
      <FieldRow>
        <TextField
          label="Views a clip"
          name="views"
          type="number"
          min={0}
          max={100_000_000}
          step={1_000}
          value={String(views)}
          onChange={(event) => setViews(clampNumber(event.target.value, 0, 100_000_000))}
        />
        <TextField
          label="Clips a month"
          name="clips"
          type="number"
          min={0}
          max={500}
          value={String(clips)}
          onChange={(event) => setClips(clampNumber(event.target.value, 0, 500))}
        />
        {ratesDiffer && !editableRate && (
          <SelectField
            label="Platform"
            name="platform"
            value={platform}
            onChange={(event) => setPlatform(event.target.value as Platform)}
          >
            {rates.map((row) => (
              <option key={row.platform} value={row.platform}>
                {PLATFORM_LABELS[row.platform]} — {formatCents(row.ratePerThousandCents)}
              </option>
            ))}
          </SelectField>
        )}
        {editableRate && (
          <TextField
            label="Rate per 1,000 views"
            name="rate"
            type="number"
            min={0}
            max={100_000}
            step={10}
            value={String(rate)}
            hint="In cents. Campaigns set their own."
            onChange={(event) => setRate(clampNumber(event.target.value, 0, 100_000))}
          />
        )}
      </FieldRow>

      <Facts>
        <Fact label="A clip at that size">{formatCents(perClip)}</Fact>
        <Fact label={`${formatNumber(Math.max(0, clips))} of them`}>{formatCents(perMonth)}</Fact>
        <Fact label="Rate used">
          {formatCents(effectiveRate)} per 1,000 verified views
          {ratesDiffer && platform ? ` on ${PLATFORM_LABELS[platform]}` : ''}
        </Fact>
        {perSubmissionCapCents !== null && (
          <Fact label="Most per clip">{formatCents(perSubmissionCapCents)}</Fact>
        )}
      </Facts>

      <p className="muted" id={captionId}>
        This is arithmetic, not an offer. You are paid on VERIFIED views, counted after the
        measurement window closes, and only on clips that pass review — a campaign's budget can
        also run out before your clip does.
      </p>
    </div>
  );
}

/** A number input a person can empty, backspace through, or paste rubbish into. */
function clampNumber(raw: string, min: number, max: number): number {
  const value = Number.parseInt(raw, 10);
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}
